const SQUEEZY_CONFIG = {
	/** Espacio uniforme entre tarjetas visibles. */
	cardGap: 16,
	/** Columnas colapsadas visibles junto a la expandida (índices 1–6). */
	visibleCollapsedCount: 6,
	/** Fracción del ancho colapsado total al hacer hover en una columna. */
	collapsedHoverStretchShare: 0.28,
	borderRadius: 6,
	blurValue: 4
} as const

interface SqueezyCanvasItem {
	imgSrc: string
	collapsedOffsetX?: number
}

interface SqueezyCard {
	itemIndex: number
	img: HTMLImageElement
	x: number
	collapsedOffsetX: number
	width: number
	leftGap: number
	blur: number
	columnIndex: number
	animationStartCollapsedOffsetX?: number
	animationStartWidth?: number
	animationStartLeftGap?: number
	targetCollapsedOffsetX?: number
	targetWidth?: number
	targetLeftGap?: number
	targetBlur?: number
}

interface SqueezyImagesCanvasOptions {
	canvasElement: HTMLCanvasElement
	items: SqueezyCanvasItem[]
	onNextColumnClick: (steps: number) => void
	prefersReducedMotion?: boolean
}

function clamp(value: number, min: number, max: number): number {
	return Math.max(Math.min(value, max), min)
}

/** cubic-bezier(0.3, 0, 0.2, 1) — curva de easing de Stripe HDS */
function easeStandard(t: number): number {
	return 1 - Math.pow(1 - t, 3)
}

function isCollapsedColumn(columnIndex: number): boolean {
	return columnIndex >= 1 && columnIndex <= SQUEEZY_CONFIG.visibleCollapsedCount
}

class CanvasImage {
	readonly imageElement = new Image()
	private loadingState: "idle" | "loading" | "loaded" | "error" = "idle"

	constructor(
		url: string,
		private readonly onImageLoad?: () => void
	) {
		this.imageElement.onload = () => {
			this.loadingState = "loaded"
			this.onImageLoad?.()
		}
		this.imageElement.onerror = () => {
			this.loadingState = "error"
		}
		this.imageElement.src = url
		if (this.imageElement.complete && this.imageElement.naturalWidth > 0) {
			this.loadingState = "loaded"
		}
	}

	loadImage(): void {
		if (this.loadingState === "loaded" || this.loadingState === "loading") return
		this.loadingState = "loading"
		if (this.imageElement.complete) {
			this.loadingState = this.imageElement.naturalWidth > 0 ? "loaded" : "error"
		}
	}
}

export class SqueezyImagesCanvas {
	private readonly canvasElement: HTMLCanvasElement
	private readonly ctx: CanvasRenderingContext2D
	private readonly dpr: number
	private readonly prefersReducedMotion: boolean
	private readonly onNextColumnClick: (steps: number) => void

	private canvasImages: CanvasImage[] = []
	private itemsCollapsedOffsetX: number[] = []
	private cards: SqueezyCard[] = []
	private hasInitializedCards = false

	private canvasWidth = 0
	private canvasHeight = 0
	private colOneBaseWidth = 0
	private collapsedColumnWidth = 0
	private columnIndexOffset = 0
	private cardsOffsetX = 0
	private animationStartCardsOffsetX = 0
	private targetCardsOffsetX = 0
	private animationDuration = 1000
	private animationStartAt = 0
	private animationRequestId: number | null = null
	private hoveredCardIndex = -1

	private readonly handleMouseEnter = (event: MouseEvent): void => this.updateHoverIndex(event)
	private readonly handleMouseMove = (event: MouseEvent): void => this.updateHoverIndex(event)
	private readonly handleMouseLeave = (): void => this.resetHoverIndex()
	private readonly handleClick = (): void => {
		if (this.hoveredCardIndex > 0) {
			this.onNextColumnClick(this.hoveredCardIndex)
		}
	}
	private readonly handleVisibilityChange = (): void => {
		if (document.visibilityState === "visible") {
			this.renderGridCards()
		}
	}

	constructor({ canvasElement, items, onNextColumnClick, prefersReducedMotion = false }: SqueezyImagesCanvasOptions) {
		this.canvasElement = canvasElement
		this.canvasElement.style.removeProperty("display")
		const context = canvasElement.getContext("2d")
		if (!context) {
			throw new Error("No se pudo obtener el contexto 2D del canvas")
		}
		this.ctx = context
		this.dpr = window.devicePixelRatio || 1
		this.prefersReducedMotion = prefersReducedMotion
		if (this.prefersReducedMotion) {
			this.animationDuration = 200
		}
		this.onNextColumnClick = onNextColumnClick

		this.canvasElement.addEventListener("mouseenter", this.handleMouseEnter)
		this.canvasElement.addEventListener("mousemove", this.handleMouseMove)
		this.canvasElement.addEventListener("mouseleave", this.handleMouseLeave)
		this.canvasElement.addEventListener("click", this.handleClick)
		document.addEventListener("visibilitychange", this.handleVisibilityChange)

		this.init(items)
		this.onCanvasResize()
	}

	loadCanvasImages(): void {
		this.canvasImages.forEach(image => image.loadImage())
	}

	onCanvasResize(): void {
		const { width, height } = this.canvasElement.getBoundingClientRect()
		this.canvasElement.width = width * this.dpr
		this.canvasElement.height = height * this.dpr
		this.canvasWidth = width
		this.canvasHeight = height
		this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0)

		this.colOneBaseWidth = this.canvasHeight
		const totalGaps = SQUEEZY_CONFIG.visibleCollapsedCount * SQUEEZY_CONFIG.cardGap
		const totalCollapsedWidth = this.canvasWidth - this.colOneBaseWidth - totalGaps
		this.collapsedColumnWidth = totalCollapsedWidth / SQUEEZY_CONFIG.visibleCollapsedCount

		if (this.animationRequestId !== null) {
			cancelAnimationFrame(this.animationRequestId)
		}

		this.cards.forEach(card => {
			card.width = this.getCardWidth(card.columnIndex)
			card.leftGap = card.columnIndex === 0 ? 0 : SQUEEZY_CONFIG.cardGap
		})

		if (this.hasInitializedCards) {
			this.resetHoverIndex()
		}
	}

	gotoPrev(steps: number): void {
		if (this.canvasImages.length === 0 || this.cards.length === 0) return

		for (let i = 0; i < steps; i += 1) {
			const leadingCard = this.cards[0]
			if (!leadingCard) break
			const itemIndex = (leadingCard.itemIndex - 1 + this.canvasImages.length) % this.canvasImages.length
			const canvasImage = this.canvasImages[itemIndex]
			if (!canvasImage) continue
			this.cards.unshift({
				itemIndex,
				img: canvasImage.imageElement,
				x: 0,
				collapsedOffsetX: 0,
				width: this.collapsedColumnWidth,
				leftGap: SQUEEZY_CONFIG.cardGap,
				blur: 0,
				columnIndex: 0
			})
		}
		this.cards.forEach((card, index) => {
			card.columnIndex = index + this.columnIndexOffset
		})
		this.startCarouselAnimation(steps)
	}

	gotoNext(steps: number): void {
		for (let i = 0; i < steps; i += 1) {
			const lastCard = this.cards.at(-1)
			if (!lastCard) break
			const itemIndex = (lastCard.itemIndex + 1) % this.canvasImages.length
			const canvasImage = this.canvasImages[itemIndex]
			if (!canvasImage) break
			this.cards.push({
				itemIndex,
				img: canvasImage.imageElement,
				x: 0,
				collapsedOffsetX: this.itemsCollapsedOffsetX[itemIndex] ?? 0,
				width: this.collapsedColumnWidth,
				leftGap: SQUEEZY_CONFIG.cardGap,
				blur: 0,
				columnIndex: 0
			})
		}
		this.columnIndexOffset -= steps
		this.cards.forEach((card, index) => {
			card.columnIndex = index + this.columnIndexOffset
		})
		this.startCarouselAnimation()
	}

	dispose(): void {
		if (this.animationRequestId !== null) {
			cancelAnimationFrame(this.animationRequestId)
		}
		this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
		this.canvasElement.width = 0
		this.canvasElement.height = 0
		this.canvasImages.forEach(image => {
			image.imageElement.onload = null
			image.imageElement.onerror = null
			image.imageElement.src = ""
		})
		this.canvasElement.removeEventListener("mouseenter", this.handleMouseEnter)
		this.canvasElement.removeEventListener("mousemove", this.handleMouseMove)
		this.canvasElement.removeEventListener("mouseleave", this.handleMouseLeave)
		this.canvasElement.removeEventListener("click", this.handleClick)
		document.removeEventListener("visibilitychange", this.handleVisibilityChange)
	}

	private init(items: SqueezyCanvasItem[]): void {
		this.canvasImages = items.map(item => new CanvasImage(item.imgSrc, () => this.renderGridCards()))
		this.itemsCollapsedOffsetX = items.map(item => item.collapsedOffsetX ?? 0)
		this.cards = items.map((_, index) => {
			const canvasImage = this.canvasImages[index]
			if (!canvasImage) {
				throw new Error(`Imagen del carrusel no encontrada en el índice ${index}`)
			}
			return {
				itemIndex: index,
				img: canvasImage.imageElement,
				x: 0,
				collapsedOffsetX: index === 0 ? 0 : (this.itemsCollapsedOffsetX[index] ?? 0),
				width: this.getCardWidth(index),
				leftGap: index === 0 ? 0 : SQUEEZY_CONFIG.cardGap,
				blur: index >= 4 ? SQUEEZY_CONFIG.blurValue : 0,
				columnIndex: index
			}
		})
		this.renderGridCards()
		this.hasInitializedCards = true
	}

	private updateHoverIndex(event: MouseEvent): void {
		const rect = this.canvasElement.getBoundingClientRect()
		const offsetX = event.clientX - rect.left
		const hovered = this.cards.find(card => card.x <= offsetX && offsetX <= card.x + card.width)?.columnIndex ?? -1

		if (hovered >= 0 && hovered !== this.hoveredCardIndex) {
			this.hoveredCardIndex = hovered
			this.updateCardsFlex()
		}
	}

	private resetHoverIndex(): void {
		this.hoveredCardIndex = -1
		this.updateCardsFlex()
	}

	private updateCardsFlex(): void {
		this.canvasElement.style.cursor = this.hoveredCardIndex > 0 ? "pointer" : "default"
		this.startCarouselAnimation()
	}

	private getCarouselStepSize(): number {
		return this.collapsedColumnWidth + SQUEEZY_CONFIG.cardGap
	}

	private getCardWidth(columnIndex: number): number {
		if (columnIndex === 0) {
			return this.colOneBaseWidth
		}

		const collapsedCount = SQUEEZY_CONFIG.visibleCollapsedCount
		const totalCollapsedWidth = this.collapsedColumnWidth * collapsedCount
		const hovered = this.hoveredCardIndex

		if (!this.prefersReducedMotion && isCollapsedColumn(hovered)) {
			const stretchShare = SQUEEZY_CONFIG.collapsedHoverStretchShare
			const squeezeShare = (1 - stretchShare) / (collapsedCount - 1)
			if (isCollapsedColumn(columnIndex)) {
				return columnIndex === hovered ? totalCollapsedWidth * stretchShare : totalCollapsedWidth * squeezeShare
			}
		}

		if (isCollapsedColumn(columnIndex) || columnIndex < 0 || columnIndex > collapsedCount) {
			return this.collapsedColumnWidth
		}

		return this.collapsedColumnWidth
	}

	private startCarouselAnimation(prevSteps = 0): void {
		const stepSize = this.getCarouselStepSize()
		this.animationStartCardsOffsetX = this.cardsOffsetX - prevSteps * stepSize
		this.targetCardsOffsetX = this.columnIndexOffset * stepSize

		this.cards.forEach(card => {
			card.animationStartCollapsedOffsetX = card.collapsedOffsetX
			card.animationStartWidth = card.width
			card.animationStartLeftGap = card.leftGap
			card.targetCollapsedOffsetX = card.columnIndex === 0 ? 0 : (this.itemsCollapsedOffsetX[card.itemIndex] ?? 0)
			card.targetWidth = this.getCardWidth(card.columnIndex)
			card.targetLeftGap = card.columnIndex === 0 ? 0 : SQUEEZY_CONFIG.cardGap
			card.targetBlur = card.columnIndex >= 4 ? SQUEEZY_CONFIG.blurValue : 0
		})

		this.animationStartAt = performance.now()
		if (this.animationRequestId !== null) {
			cancelAnimationFrame(this.animationRequestId)
		}
		this.animationRequestId = requestAnimationFrame(this.animate)
	}

	private readonly animate = (timestamp: number): void => {
		const progress = Math.min((timestamp - this.animationStartAt) / this.animationDuration, 1)
		const eased = easeStandard(progress)

		this.cardsOffsetX = this.lerp(this.animationStartCardsOffsetX, this.targetCardsOffsetX, eased)

		this.cards.forEach(card => {
			card.collapsedOffsetX = this.lerp(
				card.animationStartCollapsedOffsetX ?? card.collapsedOffsetX,
				card.targetCollapsedOffsetX ?? card.collapsedOffsetX,
				eased
			)
			card.width = this.lerp(card.animationStartWidth ?? card.width, card.targetWidth ?? card.width, eased)
			card.leftGap = this.lerp(card.animationStartLeftGap ?? card.leftGap, card.targetLeftGap ?? card.leftGap, eased)
		})

		this.renderGridCards()

		if (progress < 1) {
			this.animationRequestId = requestAnimationFrame(this.animate)
		} else {
			this.onAnimationComplete()
		}
	}

	private onAnimationComplete(): void {
		const firstVisible = this.cards.findIndex(card => card.columnIndex === 0)
		const visibleCount = 1 + SQUEEZY_CONFIG.visibleCollapsedCount
		if (firstVisible >= 0) {
			this.cards = this.cards.slice(firstVisible, firstVisible + visibleCount)
		}
		this.columnIndexOffset = 0
		this.cardsOffsetX = 0
		this.renderGridCards()
	}

	private lerp(start: number, end: number, t: number): number {
		return start + (end - start) * t
	}

	private renderGridCards(): void {
		this.cards.forEach((card, index) => {
			if (index === 0) {
				card.x = this.cardsOffsetX
				return
			}
			const prev = this.cards[index - 1]
			if (!prev) return
			card.x = prev.x + prev.width + card.leftGap
		})

		this.ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight)
		this.cards.forEach(card => {
			if (card.x + card.width < 0 || card.x > this.canvasWidth) return
			this.renderCard(card)
		})
	}

	private renderCard(card: SqueezyCard): void {
		const { img, width, x, collapsedOffsetX, columnIndex } = card
		if (!img.complete || img.naturalWidth === 0) return

		this.ctx.save()
		this.setRoundedRectClip(x, width)

		const imageAspect = img.width / img.height
		const containerAspect = width / this.canvasHeight
		let drawWidth: number
		let drawHeight: number
		let drawY: number

		if (imageAspect > containerAspect) {
			drawHeight = this.canvasHeight
			drawWidth = this.canvasHeight * imageAspect
			drawY = 0
		} else {
			drawWidth = width
			drawHeight = width / imageAspect
			drawY = (this.canvasHeight - drawHeight) / 2
		}

		// Centrar el recorte en la columna (object-cover); collapsedOffsetX solo para parallax opcional.
		const parallaxOffset = columnIndex === 0 ? 0 : collapsedOffsetX
		const drawX = x + width / 2 - drawWidth / 2 - parallaxOffset
		this.ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight)
		this.ctx.restore()
	}

	private setRoundedRectClip(x: number, width: number): void {
		const radius = clamp(SQUEEZY_CONFIG.borderRadius, 0, Math.min(width, this.canvasHeight) / 2)
		this.ctx.beginPath()
		this.ctx.roundRect(x, 0, width, this.canvasHeight, radius)
		this.ctx.clip()
	}
}
